import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  Landmark, Plus, Edit, Trash2, Calendar, HardDrive, Loader2, Download, Users, File
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';

// Import vault components
import {
  BankEntryModal,
  WillsEntryModal,
  MutualFundsEntryModal,
  InsuranceEntryModal,
  CryptoEntryModal,
  PropertyEntryModal,
  DematEntryModal,
  OthersEntryModal,
  vaultCategories,
  colorClasses,
  API,
  getAuthHeader,
  formatFileSize,
  formatDate
} from '../../components/vault';

const ClientLifeVault = () => {
  const { subdomain } = useOutletContext() || {};
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit' | 'view'
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchEntries(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/client/vault/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data.category_counts || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async (category) => {
    setEntriesLoading(true);
    try {
      const response = await axios.get(`${API}/client/vault/entries?category=${category}`, {
        headers: getAuthHeader()
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load entries');
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleCardClick = (category) => {
    setSelectedCategory(category.id);
  };

  const handleAddEntry = () => {
    setSelectedEntry(null);
    setModalMode('add');
  };

  const handleEditEntry = (entry) => {
    setSelectedEntry(entry);
    setModalMode('edit');
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      await axios.delete(`${API}/client/vault/entry/${entryId}`, {
        headers: getAuthHeader()
      });
      toast.success('Entry deleted successfully');
      fetchEntries(selectedCategory);
      fetchStats();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedEntry(null);
  };

  const handleSaveEntry = async (formData, pendingFiles = []) => {
    setSaving(true);
    try {
      let entryId = selectedEntry?.id;
      
      if (modalMode === 'add') {
        const response = await axios.post(`${API}/client/vault/entry`, {
          category: selectedCategory,
          save_as: formData.save_as,
          data: formData
        }, { headers: getAuthHeader() });
        entryId = response.data.entry_id;
        toast.success('Entry added successfully');
      } else {
        await axios.put(`${API}/client/vault/entry/${selectedEntry.id}`, {
          save_as: formData.save_as,
          data: formData
        }, { headers: getAuthHeader() });
        toast.success('Entry updated successfully');
      }
      
      // Upload any pending files
      if (pendingFiles.length > 0 && entryId) {
        for (const file of pendingFiles) {
          try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            await axios.post(
              `${API}/client/vault/entry/${entryId}/upload`,
              formDataUpload,
              {
                headers: {
                  ...getAuthHeader(),
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
          } catch (uploadError) {
            console.error('File upload error:', uploadError);
            toast.error(`Failed to upload ${file.name}`);
          }
        }
        if (pendingFiles.length > 0) {
          toast.success(`${pendingFiles.length} file(s) uploaded`);
        }
      }
      
      // Send all files to executor in a single email (after all uploads complete)
      if (formData.send_to_executor && formData.executor_email && entryId && selectedCategory === 'wills') {
        try {
          await axios.post(
            `${API}/client/vault/entry/${entryId}/send-to-executor`,
            {},
            { headers: getAuthHeader() }
          );
          toast.success(`Will documents sent to ${formData.executor_email}`);
        } catch (execError) {
          console.error('Executor email error:', execError);
          toast.error('Files saved but failed to email executor');
        }
      }
      
      fetchEntries(selectedCategory);
      fetchStats();
      closeModal();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/client/vault/download-all`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Life_Vault_Data.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download complete!');
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 404) {
        toast.error('No vault entries to download');
      } else {
        toast.error('Failed to download vault data');
      }
    } finally {
      setDownloading(false);
    }
  };

  // Get the current category info
  const currentCategory = vaultCategories.find(c => c.id === selectedCategory);

  // Modal mapping for cleaner JSX
  const modalComponents = {
    banks: BankEntryModal,
    wills: WillsEntryModal,
    mutual_funds: MutualFundsEntryModal,
    insurance: InsuranceEntryModal,
    crypto: CryptoEntryModal,
    property: PropertyEntryModal,
    demat: DematEntryModal,
    others: OthersEntryModal
  };

  const SelectedModal = modalMode && selectedCategory ? modalComponents[selectedCategory] : null;

  return (
    <div className="space-y-8" data-testid="client-life-vault">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Life Vault</h1>
          <p className="text-slate-400 mt-1">Securely store important financial information and documents</p>
        </div>
        <Button
          onClick={handleDownloadAll}
          disabled={downloading || Object.keys(stats).length === 0}
          className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
          data-testid="download-all-btn"
        >
          {downloading ? (
            <>
              <Loader2 size={18} className="mr-2 animate-spin" /> Downloading...
            </>
          ) : (
            <>
              <Download size={18} className="mr-2" /> Download All
            </>
          )}
        </Button>
      </div>

      {/* Category Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {loading ? (
          // Skeleton loading state for category cards
          <>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Card key={i} className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col items-center text-center gap-3">
                    <Skeleton className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-slate-800" />
                    <div className="space-y-2 w-full">
                      <Skeleton className="h-4 w-20 mx-auto bg-slate-800" />
                      <Skeleton className="h-3 w-16 mx-auto bg-slate-800" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          // Actual category cards
          vaultCategories.map((category) => {
            const Icon = category.icon;
            const colors = colorClasses[category.color];
            const count = stats[category.id] || 0;
            const isSelected = selectedCategory === category.id;

            return (
              <Card 
                key={category.id}
                onClick={() => handleCardClick(category)}
                className={`cursor-pointer transition-all duration-300 bg-slate-900/50 border-2 ${
                  isSelected 
                    ? `${colors.border} ${colors.bg}` 
                    : `border-slate-800 ${colors.hover}`
                }`}
                data-testid={`vault-card-${category.id}`}
              >
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col items-center text-center gap-3">
                    <div className={`p-3 rounded-xl ${colors.bg}`}>
                      <Icon className={`w-6 h-6 sm:w-8 sm:h-8 ${colors.text}`} />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-sm sm:text-base">{category.label}</h3>
                      <p className="text-slate-500 text-xs sm:text-sm mt-1">{count} entries</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Selected Category Details */}
      {selectedCategory && (
        <Card className="bg-slate-900/50 border-slate-800">
          <div className="p-6 border-b border-slate-800">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                {currentCategory && (
                  <>
                    <div className={`p-2 rounded-lg ${colorClasses[currentCategory.color].bg}`}>
                      <currentCategory.icon className={`w-5 h-5 ${colorClasses[currentCategory.color].text}`} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">{currentCategory.label}</h2>
                      <p className="text-slate-400 text-sm">{currentCategory.description}</p>
                    </div>
                  </>
                )}
              </div>
              <Button
                onClick={handleAddEntry}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="add-entry-btn"
              >
                <Plus size={18} className="mr-2" /> Add Entry
              </Button>
            </div>
          </div>

          <CardContent className="p-6">
            {entriesLoading ? (
              // Skeleton loading state for entries
              <div className="space-y-3" data-testid="entries-loading-skeleton">
                {[1, 2, 3].map((i) => (
                  <div 
                    key={i}
                    className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700"
                  >
                    <div className="flex items-center gap-4">
                      <Skeleton className="w-9 h-9 rounded-lg bg-slate-700" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-32 bg-slate-700" />
                        <Skeleton className="h-3 w-24 bg-slate-700" />
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Skeleton className="w-8 h-8 rounded bg-slate-700" />
                      <Skeleton className="w-8 h-8 rounded bg-slate-700" />
                    </div>
                  </div>
                ))}
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12">
                <div className={`w-16 h-16 rounded-2xl ${colorClasses[currentCategory?.color || 'slate'].bg} flex items-center justify-center mx-auto mb-4`}>
                  {currentCategory && <currentCategory.icon className={`w-8 h-8 ${colorClasses[currentCategory.color].text}`} />}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">No entries yet</h3>
                <p className="text-slate-400 text-sm mb-4">Add your first {currentCategory?.label.toLowerCase()} entry</p>
                <Button
                  onClick={handleAddEntry}
                  variant="outline"
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                >
                  <Plus size={16} className="mr-2" /> Add Entry
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {entries.map((entry) => (
                  <div 
                    key={entry.id}
                    className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-slate-600 transition-all"
                    data-testid={`entry-${entry.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${colorClasses[currentCategory?.color || 'slate'].bg}`}>
                        <File className={`w-5 h-5 ${colorClasses[currentCategory?.color || 'slate'].text}`} />
                      </div>
                      <div>
                        <h4 className="text-white font-medium flex items-center gap-2">
                          {entry.save_as}
                          {entry.share_with_advisor && (
                            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Users size={10} /> Shared
                            </span>
                          )}
                        </h4>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-slate-500 text-xs flex items-center gap-1">
                            <Calendar size={12} />
                            {formatDate(entry.updated_at || entry.created_at)}
                          </span>
                          {entry.files?.length > 0 && (
                            <span className="text-slate-500 text-xs flex items-center gap-1">
                              <HardDrive size={12} />
                              {entry.files.length} file(s) • {formatFileSize(entry.files.reduce((sum, f) => sum + (f.size || 0), 0))}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditEntry(entry)}
                        className="text-slate-400 hover:text-white hover:bg-slate-700"
                        data-testid={`edit-entry-${entry.id}`}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteEntry(entry.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-entry-${entry.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Initial State - No category selected */}
      {!selectedCategory && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto mb-6">
              <Landmark className="w-10 h-10 text-amber-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Select a Category</h3>
            <p className="text-slate-400 max-w-md mx-auto">
              Click on any of the cards above to view, add, or manage your financial information securely.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Dynamic Modal Rendering */}
      {SelectedModal && (
        <SelectedModal
          mode={modalMode}
          entry={selectedEntry}
          onClose={closeModal}
          onSave={handleSaveEntry}
          onRefresh={() => {
            fetchEntries(selectedCategory);
            fetchStats();
          }}
          saving={saving}
        />
      )}
    </div>
  );
};

export default ClientLifeVault;
